/**
 * CR% par canal ou par pays, en barres triées : achats GA4 ÷ sessions.
 *
 * Les valeurs à moins de `minSessions` sessions sont écartées : trois achats
 * sur vingt sessions font 15 % et écraseraient l'échelle sans rien dire.
 */

import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, BAR_RADIUS, CATEGORICAL_COLORS, COLORS } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';

interface Entry {
  label: string;
  conversion: number;
  purchases: number;
  sessions: number;
}

export function Ga4ConversionBars({
  title,
  rows,
  minSessions,
  max = 12,
}: {
  title: string;
  rows: ReadonlyArray<{ label: string; sessions: number; purchases: number; conversionPercent: number | null }>;
  minSessions: number;
  max?: number;
}) {
  const entries: Entry[] = rows
    .filter((row) => row.sessions >= minSessions && row.conversionPercent !== null)
    .map((row) => ({ label: row.label, conversion: row.conversionPercent ?? 0, purchases: row.purchases, sessions: row.sessions }))
    .sort((a, b) => b.conversion - a.conversion)
    .slice(0, max);

  const columns: ReadonlyArray<DataColumn<Entry>> = [
    { key: 'label', header: '', render: (row) => row.label },
    { key: 'conversion', header: 'CR%', numeric: true, render: (row) => formatPercent(row.conversion) },
    { key: 'purchases', header: 'Achats', numeric: true, render: (row) => formatInteger(row.purchases) },
    { key: 'sessions', header: 'Sessions', numeric: true, render: (row) => formatInteger(row.sessions) },
  ];
  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };

  return (
    <ChartCard
      title={title}
      subtitle={`Achats GA4 ÷ sessions, du plus haut au plus bas — à partir de ${formatInteger(minSessions)} sessions`}
      rows={entries}
      columns={columns}
      emptyMessage="Pas assez de sessions sur cette période."
      chartHeight={300}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={entries} margin={{ top: 8, right: 12, bottom: 24, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="label" tick={{ ...axisTick, fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={60} axisLine={{ stroke: COLORS.grid }} tickLine={false} />
          <YAxis tickFormatter={(value: number) => formatPercent(value)} tick={axisTick} axisLine={false} tickLine={false} width={52} />
          <Tooltip
            formatter={(value) => formatPercent(Number(value))}
            contentStyle={{ background: COLORS.surface, border: `1px solid ${COLORS.grid}` }}
            cursor={{ fill: COLORS.grid, opacity: 0.4 }}
          />
          <Bar dataKey="conversion" name="CR%" fill={CATEGORICAL_COLORS[0]} radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]} maxBarSize={BAR_MAX_SIZE * 2} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
